import React, { useState, useEffect, useRef, useCallback } from "react";
import { User, Lock, Mail, TrendingUp, LineChart, ShieldCheck, ArrowRight, ArrowLeft } from "lucide-react";
import { login, googleLogin, register, restoreAccount, restoreGoogleAccount } from "../api";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const inputStyle = {
  backgroundColor: "var(--bg-page)",
  border: "1px solid var(--border-color)",
  color: "var(--text-primary)",
  borderRadius: "10px",
  padding: "11px 12px 11px 38px",
  fontSize: "14px",
  outline: "none",
  width: "100%",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const FEATURES = [
  { icon: LineChart, text: "Real-time technical & fundamental analysis" },
  { icon: TrendingUp, text: "AI research team synthesizing every signal" },
  { icon: ShieldCheck, text: "Portfolio, wallet & risk tracking in one place" },
];

function FieldIcon({ Icon }) {
  return (
    <Icon
      size={16}
      style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }}
    />
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function Login({ onSuccess, onBack, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode); // "login" | "register"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [restoreInfo, setRestoreInfo] = useState(null); // { deletedAt, restoreBy, kind: "password" | "google", googleCredential? }
  const googleBtnRef = useRef(null);

  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setPassword("");
    setConfirmPassword("");
  };

  const handleGoogleCredential = useCallback(async (response) => {
    setError(null);
    setSubmitting(true);
    try {
      const data = await googleLogin(response.credential);
      if (data.restorable) {
        setRestoreInfo({ deletedAt: data.deleted_at, restoreBy: data.restore_by, kind: "google", googleCredential: response.credential });
        return;
      }
      onSuccess();
    } catch (err) {
      setError(err.message || "Google sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  }, [onSuccess]);

  const handleRestoreConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (restoreInfo.kind === "google") {
        await restoreGoogleAccount(restoreInfo.googleCredential);
      } else {
        await restoreAccount(username, password);
      }
      onSuccess();
    } catch (err) {
      setError(err.message || "Couldn't restore this account.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    const tryInit = () => {
      if (cancelled) return;
      if (window.google?.accounts?.id && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: "filled_black",
          size: "large",
          width: 316,
          text: "continue_with",
        });
      } else {
        setTimeout(tryInit, 100);
      }
    };
    tryInit();
    return () => { cancelled = true; };
  }, [handleGoogleCredential]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError("Please enter your username and password.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const data = await login(username, password);
      if (data.restorable) {
        setRestoreInfo({ deletedAt: data.deleted_at, restoreBy: data.restore_by, kind: "password" });
        return;
      }
      onSuccess();
    } catch (err) {
      setError(err.message || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password || !email) {
      setError("Please fill in username, email, and password.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await register(username, password, email, fullName);
      onSuccess();
    } catch (err) {
      setError(err.message || "Couldn't create your account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen" style={{ backgroundColor: "var(--bg-page)" }}>
      {/* Brand panel */}
      <div
        className="hidden lg:flex flex-col justify-between w-[46%] p-12 relative overflow-hidden"
        style={{
          background: "radial-gradient(ellipse 900px 600px at 20% 10%, rgba(67, 97, 238, 0.22), transparent 60%), radial-gradient(ellipse 700px 500px at 90% 90%, rgba(217, 166, 63, 0.10), transparent 55%), var(--bg-page)",
          borderRight: "1px solid var(--bg-secondary)",
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold"
              style={{ background: "linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))", boxShadow: "0 8px 24px -6px rgba(67, 97, 238, 0.7)" }}
            >
              ST
            </div>
            <div>
              <div className="font-bold text-lg tracking-tight" style={{ color: "var(--text-primary)" }}>SwingTrader</div>
              <div className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "#d9a63f" }}>Pro</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-4xl font-bold leading-tight tracking-tight mb-4" style={{ color: "var(--text-primary)" }}>
            Trade with an edge,<br />not a guess.
          </h1>
          <p className="text-sm leading-relaxed mb-8" style={{ color: "var(--text-secondary)" }}>
            Screeners, live technicals, fundamentals and an AI research team —
            built for swing traders who want signal, not noise.
          </p>
          <div className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "rgba(67, 97, 238, 0.15)", border: "1px solid rgba(67, 97, 238, 0.3)" }}
                >
                  <Icon size={15} style={{ color: "var(--link-blue)" }} />
                </div>
                <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs" style={{ color: "#4a5170" }}>
          US Market · Swing trading signals &amp; analytics
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-4 relative">
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-5 left-5 flex items-center gap-1.5 text-sm font-medium transition-colors"
            style={{ color: "var(--text-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
          >
            <ArrowLeft size={15} />
            Back to home
          </button>
        )}
        {restoreInfo ? (
          <div
            className="rounded-2xl p-8 w-full max-w-sm animate-fade-in-up"
            style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)", boxShadow: "0 1px 2px rgba(0,0,0,0.3), 0 24px 48px -20px rgba(0,0,0,0.7)" }}
          >
            <h2 className="text-xl font-bold mb-1" style={{ color: "var(--text-primary)" }}>Restore your account?</h2>
            <p className="text-sm mt-3 mb-2" style={{ color: "var(--text-secondary)" }}>
              This account was deleted on <span style={{ color: "var(--text-primary)" }}>{fmtDate(restoreInfo.deletedAt)}</span>.
              It can still be restored — your data wasn't removed.
            </p>
            <p className="text-xs mb-6" style={{ color: "var(--text-muted)" }}>
              The restore window closes on {fmtDate(restoreInfo.restoreBy)}.
            </p>

            {error && (
              <div className="mb-4 text-sm rounded-lg px-3 py-2" style={{ color: "var(--text-red-soft)", backgroundColor: "var(--bg-red-soft)", border: "1px solid var(--loss-red)" }}>
                {error}
              </div>
            )}

            <button
              onClick={handleRestoreConfirm}
              disabled={submitting}
              className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all mb-2.5"
              style={{ background: "linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))", color: "#fff", opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? "Restoring..." : "Restore my account"}
            </button>
            <button
              onClick={() => { setRestoreInfo(null); setError(null); }}
              className="w-full py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: "transparent", color: "var(--text-muted)" }}
            >
              Cancel
            </button>
          </div>
        ) : (
        <form
          onSubmit={mode === "register" ? handleRegisterSubmit : handleSubmit}
          className="rounded-2xl p-8 w-full max-w-sm animate-fade-in-up"
          style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)", boxShadow: "0 1px 2px rgba(0,0,0,0.3), 0 24px 48px -20px rgba(0,0,0,0.7)" }}
        >
          <div className="flex items-center gap-2 mb-1 lg:hidden">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background: "linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))" }}
            >
              ST
            </div>
            <div>
              <div className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>SwingTrader</div>
              <div className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "#d9a63f" }}>Pro</div>
            </div>
          </div>

          <h2 className="text-xl font-bold mb-1 mt-3 lg:mt-0" style={{ color: "var(--text-primary)" }}>
            {mode === "register" ? "Create your account" : "Welcome back"}
          </h2>
          <p className="text-xs mb-6" style={{ color: "var(--text-muted)" }}>
            {mode === "register" ? "Start swing trading with real signals." : "Sign in to continue to your dashboard."}
          </p>

          {GOOGLE_CLIENT_ID && (
            <>
              <div ref={googleBtnRef} className="mb-4 flex justify-center" />
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px" style={{ backgroundColor: "var(--border-color)" }} />
                <span className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>or</span>
                <div className="flex-1 h-px" style={{ backgroundColor: "var(--border-color)" }} />
              </div>
            </>
          )}

          {mode === "register" && (
            <div className="mb-4">
              <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                Email
              </label>
              <div className="relative">
                <FieldIcon Icon={Mail} />
                <input
                  type="email"
                  style={inputStyle}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent-blue)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(67,97,238,0.15)"; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border-color)"; e.currentTarget.style.boxShadow = "none"; }}
                />
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
              Username
            </label>
            <div className="relative">
              <FieldIcon Icon={User} />
              <input
                style={inputStyle}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus={mode === "login"}
                onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent-blue)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(67,97,238,0.15)"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border-color)"; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
          </div>

          {mode === "register" && (
            <div className="mb-4">
              <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                Full name <span style={{ color: "#4a5170" }}>(optional)</span>
              </label>
              <div className="relative">
                <FieldIcon Icon={User} />
                <input
                  style={inputStyle}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent-blue)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(67,97,238,0.15)"; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border-color)"; e.currentTarget.style.boxShadow = "none"; }}
                />
              </div>
            </div>
          )}

          <div className={mode === "register" ? "mb-4" : "mb-5"}>
            <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
              Password
            </label>
            <div className="relative">
              <FieldIcon Icon={Lock} />
              <input
                type="password"
                style={inputStyle}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent-blue)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(67,97,238,0.15)"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border-color)"; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
          </div>

          {mode === "register" && (
            <div className="mb-5">
              <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                Confirm password
              </label>
              <div className="relative">
                <FieldIcon Icon={Lock} />
                <input
                  type="password"
                  style={inputStyle}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent-blue)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(67,97,238,0.15)"; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border-color)"; e.currentTarget.style.boxShadow = "none"; }}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 text-sm rounded-lg px-3 py-2" style={{ color: "var(--text-red-soft)", backgroundColor: "var(--bg-red-soft)", border: "1px solid var(--loss-red)" }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1.5 group"
            style={{
              background: "linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))",
              color: "#fff",
              opacity: submitting ? 0.7 : 1,
              cursor: submitting ? "default" : "pointer",
              boxShadow: submitting ? "none" : "0 8px 20px -6px rgba(67, 97, 238, 0.6)",
            }}
          >
            {mode === "register"
              ? (submitting ? "Creating account..." : (<>Create Account<ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" /></>))
              : (submitting ? "Signing in..." : (<>Sign In<ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" /></>))}
          </button>

          <div className="text-center text-xs mt-4" style={{ color: "var(--text-muted)" }}>
            {mode === "register" ? (
              <>Already have an account?{" "}
                <button type="button" onClick={() => switchMode("login")} className="font-medium" style={{ color: "var(--link-blue)" }}>
                  Log in
                </button>
              </>
            ) : (
              <>New here?{" "}
                <button type="button" onClick={() => switchMode("register")} className="font-medium" style={{ color: "var(--link-blue)" }}>
                  Create an account
                </button>
              </>
            )}
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
